import {
  Cryoprotectantv4,
  CrystalCryoDTO,
  CrystalSoakingDTO,
  Soakingv4,
} from 'business/models/Models';
import CryoprotectantService from 'business/services/CryoprotectantService';
import SoakingService from 'business/services/SoakingService';
import { useMemo } from 'react';
import { useRMDispatch } from 'redux/hooks';
import {
  useCrystals,
  useCurrentTripCheckedCrystals,
  useCurrentWell,
} from 'redux/hooks/crystal';
import { useCryoprotectants, useSoakings } from 'redux/hooks/synchrotron';
import { setHarvestingContentView } from 'redux/reducers/synchrotronTripReducer';
import { EHarvestingView } from '../constants';
import { AgentDTO } from './selectAgents';
import cloneDeep from 'lodash/cloneDeep';
import { useTabBackButtonState } from 'components/tab';
import { DrawingType } from 'components/well/drawingManager/constants';
import { updateMultipleDrawings } from 'redux/reducers/wellReducer';
import {
  useLastSelectedWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';

export const useAssignCryoToCrystals = (checkedAgents: number[]) => {
  const crystals = cloneDeep(useCrystals());
  const crystalIds = useCurrentTripCheckedCrystals();
  const cryoAgents = useCryoAgents();
  const dispatch = useRMDispatch();
  const [, setHandleOnTabBackPress] = useTabBackButtonState();
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};
  const getCrystalCryoDTO = useMemo(() => {
    return checkedAgents?.length > 0
      ? checkedAgents.flatMap((agentId) =>
          crystalIds?.map(
            (crystalId) =>
              ({
                cryoprotectantID: agentId,
                crystalId: crystalId,
              } as CrystalCryoDTO),
          ),
        )
      : crystalIds?.map(
          (crystalId) =>
            ({ cryoprotectantID: -1, crystalId: crystalId } as CrystalCryoDTO),
        );
  }, [checkedAgents, crystalIds]);

  const addCryosToCrystalList = useMemo(() => {
    const updatedCrystals = crystals?.map((crystal) => {
      if (crystalIds?.includes(crystal.id)) {
        if (checkedAgents?.length > 0) {
          crystal.cryoprotectants = checkedAgents
            ?.map((agent) => {
              const cryoAgent = cryoAgents.find((x) => x.id === agent);

              if (!cryoAgent) {
                return null;
              }

              return {
                id: agent,
                ingredient: cryoAgent.label,
              } as Cryoprotectantv4;
            })
            .filter(Boolean);
        } else {
          crystal.cryoprotectants = [];
        }
      }
      return { id: crystal.id, cryoprotectants: crystal.cryoprotectants };
    });

    return () => {
      dispatch(
        updateMultipleDrawings({
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber: dropNumber,
          regionName: regionName,
          drawingType: DrawingType.CRYSTAL,
          drawingUpdateDetails: updatedCrystals,
        }),
      );
    };
  }, [crystals, checkedAgents, cryoAgents]);

  return async () => {
    await CryoprotectantService.getInstance().assignCryosToCrystals(getCrystalCryoDTO);
    addCryosToCrystalList();
    dispatch(setHarvestingContentView(EHarvestingView.HARVESTING));
    setHandleOnTabBackPress(null);
  };
};

export const useAssignSoakingToCrystals = (checkedAgents: number[]) => {
  const crystals = cloneDeep(useCrystals());
  const crystalIds = useCurrentTripCheckedCrystals();
  const soakingAgents = useSoakingAgents();
  const dispatch = useRMDispatch();
  const [, setHandleOnTabBackPress] = useTabBackButtonState();
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};
  const getCrystalSoakingDTO = useMemo(() => {
    return checkedAgents?.length > 0
      ? checkedAgents.flatMap((agentId) =>
          crystalIds?.map(
            (crystalId) =>
              ({
                soakingId: agentId,
                crystalId: crystalId,
              } as CrystalSoakingDTO),
          ),
        )
      : crystalIds?.map(
          (crystalId) => ({ soakingId: -1, crystalId: crystalId } as CrystalSoakingDTO),
        );
  }, [checkedAgents, crystalIds]);

  const addSoakingToCrystalList = useMemo(() => {
    const updatedCrystals = crystals?.map((crystal) => {
      if (crystalIds?.includes(crystal.id)) {
        if (checkedAgents?.length > 0) {
          crystal.soakings = checkedAgents?.map(
            (agent) =>
              ({
                id: agent,
                ingredient: soakingAgents?.find((x) => x.id === agent).label,
              } as Soakingv4),
          );
        } else {
          crystal.soakings = [];
        }
      }
      return { id: crystal.id, soakings: crystal.soakings };
    });
    return () => {
      dispatch(
        updateMultipleDrawings({
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber: dropNumber,
          regionName: regionName,
          drawingType: DrawingType.CRYSTAL,
          drawingUpdateDetails: updatedCrystals,
        }),
      );
    };
  }, [checkedAgents, crystals, soakingAgents]);

  return async () => {
    await SoakingService.getInstance().assignSoakingToCrystals(getCrystalSoakingDTO);
    addSoakingToCrystalList();
    dispatch(setHarvestingContentView(EHarvestingView.HARVESTING));
    setHandleOnTabBackPress(null);
  };
};

export const useCryoAgents = () => {
  const cryoList = useCryoprotectants();
  const crystals = useCrystals();
  const crystalIds = useCurrentTripCheckedCrystals();
  const currentWell = useCurrentWell();
  const currentSelectedCrystalId = crystalIds?.length ? crystalIds[0] : -1;
  return useMemo(() => {
    return cryoList
      ?.filter((cryo) => !cryo.sourceWellId || cryo.sourceWellId === currentWell?.wellId)
      .map(
        (cryo) =>
          ({
            id: cryo.id,
            label:
              cryo.ingredient +
              (cryo.conc ? ', ' + cryo.conc + cryo.concUnit : '') +
              (cryo.pH ? ', pH ' + cryo.pH : ''),
            checked: crystals?.some(
              (x) =>
                x.id === currentSelectedCrystalId &&
                x.cryoprotectants?.some((y) => y.id === cryo.id),
            ),
          } as AgentDTO),
      );
  }, [cryoList, crystals, crystalIds, currentWell?.wellId]);
};

export const useSoakingAgents = () => {
  const soakingList = useSoakings();
  const crystals = useCrystals();
  const crystalIds = useCurrentTripCheckedCrystals();
  const currentSelectedCrystalId = crystalIds?.length ? crystalIds[0] : -1;
  return useMemo(() => {
    return soakingList?.map(
      (soaking) =>
        ({
          id: soaking.id,
          label:
            soaking.ingredient +
            (soaking.conc ? ', ' + soaking.conc + soaking.concUnit : '') +
            (soaking.time ? ', ' + soaking.time + soaking.timeUnit : ''),
          checked: crystals?.some(
            (x) =>
              x.id === currentSelectedCrystalId &&
              x.soakings?.some((y) => y.id === soaking.id),
          ),
        } as AgentDTO),
    );
  }, [soakingList, crystals, crystalIds]);
};
